import { LocaleKey } from 'translations';

/*
 * Character limits for the free-text fields of the Relining form.
 *
 * These are enforced at submit time by the form-level `validate` callback in
 * `ReliningPage` (see the "character limit" block there). When a value exceeds
 * its limit an inline error message is shown beneath the field and submission
 * is blocked, mirroring how the "Type of Equipment Used" and calculator
 * "Length of pipe" validations already work.
 *
 * The limits are grouped by the kind of input so a single change here updates
 * every field of that kind across all installation types (Liner, Spray, Branch
 * and Other), since many of these fields are shared components reused per type.
 */

/** Default maximum length for single-line text inputs. */
export const TEXT_INPUT_MAX_LENGTH = 130;

/** Default maximum length for multi-line textareas. */
export const TEXTAREA_MAX_LENGTH = 500;

/*
 * "Batch number" inputs (Liner / Component A (Base) / Component B (Hardener) /
 * the Other-type "Description" and the Spray "Spray time of day"). Requested as
 * "3 sentences max"; interpreted here as a character limit that comfortably
 * fits ~3 short sentences and sits between the text-input and textarea limits.
 * Adjust this single value if a different amount is preferred.
 */
export const BATCH_NUMBER_MAX_LENGTH = 300;

/** Translation key for the "text input too long" validation message. */
export const TEXT_INPUT_MAX_LENGTH_ERROR: LocaleKey = 'errors.characterLimit.textInput';

/** Translation key for the "textarea too long" validation message. */
export const TEXTAREA_MAX_LENGTH_ERROR: LocaleKey = 'errors.characterLimit.textarea';

/** Translation key for the "batch number too long" validation message. */
export const BATCH_NUMBER_MAX_LENGTH_ERROR: LocaleKey = 'errors.characterLimit.batchNumber';

/** Translation key for the "pipe temperature must be a number" message. */
export const PIPE_TEMPERATURE_NUMBER_ERROR: LocaleKey = 'errors.pipeTemperature.number';

/**
 * Whether a value exceeds a maximum character length.
 *
 * Empty / non-string values are treated as valid (length 0), so only real text
 * that goes over the limit is flagged.
 */
export function isOverCharLimit(value: unknown, max: number): boolean {
  if (typeof value !== 'string') {
    return false;
  }

  return value.length > max;
}

/**
 * Whether a value is present but not a valid number.
 *
 * Empty values are treated as valid (the field is optional); only a non-empty,
 * non-numeric value is flagged. Used for the "Pipe temperature" field, which
 * must be a number.
 */
export function isNotNumericValue(value: unknown): boolean {
  if (value === undefined || value === null || value === '') {
    return false;
  }

  const asString = String(value).trim();
  if (asString === '') {
    return false;
  }

  return Number.isNaN(Number(asString));
}

